import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

export type TableFilters<T extends string = string> = Record<T, string[] | undefined>;

const deserializeArray = (value: string | null): string[] => {
  if (value === null) {
    return [];
  }

  try {
    return JSON.parse(value);
  } catch {
    console.error(`Failed to deserialize array: ${value}`);
    return [];
  }
};

const deserializeFilters = <T extends string = string>(
  search: string,
  filterKeys: Array<T>
): TableFilters => {
  const searchParams = new URLSearchParams(search);

  const filters: TableFilters = {};

  for (const key of searchParams.keys()) {
    if (filterKeys.includes(key as T)) {
      filters[key] = deserializeArray(searchParams.get(key));
    }
  }

  return filters;
};

const applyFiltersToSearch = <T extends string = string>(
  search: string,
  filters: TableFilters<T>
): string => {
  const searchParams = new URLSearchParams(search);

  for (const key in filters) {
    if (filters[key] === undefined || filters[key]?.length === 0) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, JSON.stringify(filters[key]));
    }
  }

  return searchParams.toString();
};

const filtersAreEqual = <T extends string = string>(
  a: TableFilters<T>,
  b: TableFilters<T>,
  filterKeys: T[]
): boolean => filterKeys.every(key => JSON.stringify(a[key]) === JSON.stringify(b[key]));

export const useTableFilters = <T extends string = string>(filterKeys: T[]) => {
  const history = useHistory();

  const location = useLocation();

  // Stabilize the dependency so effects don't re-run on every render just
  // because the caller passes a new array literal.
  const filterKeysKey = filterKeys.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFilterKeys = useMemo(() => filterKeys, [filterKeysKey]);

  const [filters, setFilters] = useState<TableFilters<T>>(
    deserializeFilters(location.search, stableFilterKeys)
  );

  // Sync state when the URL changes externally (e.g. clicking a nav tab that strips params).
  useEffect(() => {
    const urlFilters = deserializeFilters(location.search, stableFilterKeys) as TableFilters<T>;
    setFilters(current =>
      filtersAreEqual(urlFilters, current, stableFilterKeys) ? current : urlFilters
    );
  }, [location.search, stableFilterKeys]);

  useEffect(() => {
    const nextSearch = applyFiltersToSearch(location.search, filters);

    // Nothing to do if the URL already reflects the current filters.
    if (nextSearch === new URLSearchParams(location.search).toString()) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: nextSearch,
    });
    // location.search / location.pathname are intentionally omitted: this effect
    // should only fire when `filters` changes, and it reads the latest location
    // values at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return { filters, setFilters };
};
